/**
 * cart-related-filter.js
 *
 * Hides products in cart-drawer-related-products-block that are already
 * present in the cart. Works for both auto (Shopify recommendations) and
 * manual blocks (manual handled server-side via Liquid; auto handled here).
 *
 * When a product is added or removed from the cart the theme re-renders
 * the section and re-fetches auto recommendations. Our MutationObserver
 * listens for the `is-ready` attribute being set on the related-products
 * element — that is the signal that new recommendation HTML is in the DOM —
 * and then we hide any slide whose product is already in the cart.
 */
(function () {
  'use strict';

  /** Fetch the current cart and return an array of product IDs. */
  function fetchCartProductIds() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return (cart.items || []).map(function (item) { return item.product_id; });
      })
      .catch(function () { return []; });
  }

  /**
   * Hide slides inside `block` whose product card data-id is in cartIds.
   * Re-shows slides whose product is no longer in the cart.
   */
  function filterSlides(block, cartIds) {
    var slides = block.querySelectorAll(
      '.cart-drawer__in-cart-related-products-slide, ' +
      '.cart-drawer__on-side-related-products-slide'
    );

    slides.forEach(function (slide) {
      var card = slide.querySelector('[data-id]');
      if (!card) return;
      var productId = Number(card.getAttribute('data-id'));
      slide.style.display = (cartIds.indexOf(productId) !== -1) ? 'none' : '';
    });
  }

  /** Set up a MutationObserver on the related-products inner element for `is-ready`. */
  function observeRelatedEl(block, relatedEl) {
    // Run immediately if already ready (server-rendered is-ready).
    if (relatedEl.hasAttribute('is-ready')) {
      fetchCartProductIds().then(function (ids) { filterSlides(block, ids); });
    }

    // Watch for is-ready additions (after every AJAX recommendations fetch).
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (
          mutations[i].attributeName === 'is-ready' &&
          relatedEl.hasAttribute('is-ready')
        ) {
          fetchCartProductIds().then(function (ids) { filterSlides(block, ids); });
          break;
        }
      }
    });

    mo.observe(relatedEl, { attributes: true, attributeFilter: ['is-ready'] });
  }

  /** Wire up a single block element (only auto-recommendation blocks need JS). */
  function setupBlock(block) {
    if (block._crfSetup) return;
    block._crfSetup = true;

    // Only auto blocks need JS filtering; manual blocks are filtered in Liquid.
    if (!block.hasAttribute('is-auto-recommendations')) return;

    var relatedEls = block.querySelectorAll('[data-cart-drawer-related-products]');
    relatedEls.forEach(function (el) { observeRelatedEl(block, el); });
  }

  /** Scan the document for blocks and set them up. */
  function initAllBlocks() {
    var blocks = document.querySelectorAll('cart-drawer-related-products-block');
    blocks.forEach(setupBlock);
  }

  /**
   * The cart drawer renders its content from a <template> that is stamped
   * into the DOM when the drawer first opens.  We watch the cart-drawer
   * element for new child nodes so we can wire up blocks as they appear.
   */
  function watchCartDrawer() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Wire up any blocks already in the DOM.
    initAllBlocks();

    // Watch for the template being stamped (or re-stamped after cart updates).
    var mo = new MutationObserver(function () { initAllBlocks(); });
    mo.observe(cartDrawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCartDrawer);
  } else {
    watchCartDrawer();
  }
})();

/**
 * cms-atc-btn interceptor
 *
 * The nice-bundler app renders product forms as plain <form class="shopify-product-form">
 * with no <product-form> wrapper, so submitting them does a full-page POST to /cart.
 * This intercepts those submits, performs the AJAX add via /cart/add.js (exactly as the
 * theme's ProductForm custom element does) and then calls cart-drawer.purchaseHandler()
 * to update and open the drawer — without touching any other forms or behaviour.
 */
(function () {
  'use strict';

  function parseHTML(str) {
    return (new DOMParser()).parseFromString(str, 'text/html');
  }

  function handleCmsFormSubmit(event) {
    var form = event.currentTarget;

    // Only intercept forms that have a cms-atc-btn submit button
    if (!form.querySelector('.cms-atc-btn[type="submit"]')) return;

    event.preventDefault();

    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    var sectionId = cartDrawer.dataset.sectionId;
    var submitBtn = form.querySelector('.cms-atc-btn[type="submit"]');

    // Show loading state
    if (submitBtn) submitBtn.classList.add('loading');

    var formData = new FormData(form);
    formData.append('sections', sectionId);
    formData.append('sections_url', window.location.pathname);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: formData
    })
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var parsedState = JSON.parse(text);

        // If Shopify returned an error description, bail out gracefully
        if (parsedState.description) {
          console.warn('[cms-atc-btn] Add to cart error:', parsedState.description);
          return;
        }

        var sectionHTML = parsedState.sections && parsedState.sections[sectionId];
        if (!sectionHTML) return;

        var html = parseHTML(sectionHTML);
        cartDrawer.purchaseHandler(html, parsedState);
      })
      .catch(function (err) {
        console.error('[cms-atc-btn] Fetch error:', err);
      })
      .finally(function () {
        if (submitBtn) submitBtn.classList.remove('loading');
      });
  }

  function attachCmsFormListeners() {
    var forms = document.querySelectorAll('form.shopify-product-form');
    forms.forEach(function (form) {
      if (form._cmsAtcBound) return;
      form._cmsAtcBound = true;
      form.addEventListener('submit', handleCmsFormSubmit);
    });
  }

  // Run on DOM ready and also observe for dynamically injected forms (swiper lazy)
  function init() {
    attachCmsFormListeners();

    var mo = new MutationObserver(function () { attachCmsFormListeners(); });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
